import { alpha, Components, LinearProgressProps, Theme } from '@mui/material'

declare module '@mui/material/LinearProgress' {
  interface LinearProgressProps {
    // LinearProgress has no slots API to strip custom props from the root <span>, and its root is a
    // styled('span'), so `size`/`shape` props would reach the DOM as invalid attributes. Data
    // attributes are valid there and still match in `variants`.
    'data-size'?: 'sm' | 'md' | 'lg'
    'data-shape'?: 'rounded' | 'square'
    'data-color'?: 'brand' | 'success' | 'warning' | 'danger'
  }
}

const barColor = (theme: Theme, color: NonNullable<LinearProgressProps['data-color']>) =>
  ({
    brand: theme.palette.bg.brand.default,
    success: theme.palette.bg.success.default,
    warning: theme.palette.bg.warning.default,
    danger: theme.palette.bg.danger.default,
  })[color]

export const MuiLinearProgress: Components<Omit<Theme, 'components'>>['MuiLinearProgress'] = {
  defaultProps: {
    'data-size': 'md',
    'data-shape': 'rounded',
    'data-color': 'brand',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape['radius-full'],
      backgroundColor: theme.palette.bg.neutral.muted,
    }),
    bar: ({ theme, ownerState }) => ({
      backgroundColor: barColor(theme, ownerState['data-color'] ?? 'brand'),
      borderRadius: theme.shape['radius-full'],
    }),
    // `bar` colours both bars, so the buffer bar needs its own lighter shade to read as a second
    // segment. A colour, not opacity: opacity would also fade a consumer's own bar2 colour.
    bar2: ({ theme, ownerState }) =>
      ownerState.variant === 'buffer'
        ? { backgroundColor: alpha(barColor(theme, ownerState['data-color'] ?? 'brand'), 0.3) }
        : {},
    // MUI's animated dots after the buffer mean "still loading"; the track shows there instead.
    dashed: {
      display: 'none',
    },
  },
  variants: [
    {
      props: { 'data-size': 'sm' },
      style: {
        height: '4px',
      },
    },
    {
      props: { 'data-size': 'md' },
      style: {
        height: '8px',
      },
    },
    {
      props: { 'data-size': 'lg' },
      style: {
        height: '10px',
      },
    },
    {
      props: { 'data-shape': 'square' },
      style: {
        borderRadius: 0,
        '& .MuiLinearProgress-bar': {
          borderRadius: 0,
        },
      },
    },
  ],
}
